import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Grid, Box, Typography, Tooltip, Divider } from "@mui/material";
import moment from "moment";
import { FieldProps, FormConfigProps } from "./form-builder.model";
import { FIELD_COMPONENTS } from "./form-fields.model";
import { useFormStore } from "../../../store/transaction-certificates.store";
import { useFormActions } from "../../hooks/form-builder.hooks";
import { current } from "@reduxjs/toolkit";
import { useTransactionCertificate } from "../../hooks/transaction-certificate.hooks";
import { Utills } from "../../app-configs/utilit";
import {
  ArrowDownward,
  ArrowRight,
  ChevronRight,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
Utills.cl("React version:", React.version);

interface FormBuilderProps {
  formConfig: FormConfigProps;
  onSubmit?: (values: any) => void;
  formRef?: React.Ref<any>; // Optional ref for Formik to allow parent component to control submission
}
interface SectionFlagProps {
  isOpen: boolean;
}
const FormBuilder: React.FC<FormBuilderProps> = ({
  formConfig,
  onSubmit,
  formRef,
}) => {
  const {
    initialValues,
    validationSchema,
    sections,
    pageName,
    subPageName,
    readOnly,
  } = formConfig;
  const { updateField } = pageName
    ? useFormActions(pageName)
    : { updateField: () => {} };
  const [sectionFlags, setSectionFlags] = useState<SectionFlagProps[]>(
    sections?.map(() => {
      return { isOpen: false };
    }) || []
  );
  const renderComponents = (field: FieldProps, formikProps: any) => {
    const { type, children, name, label } = field; // Include 'disabled'
    const Component = FIELD_COMPONENTS[type];
    const { values, errors, touched, setFieldValue, handleBlur } = formikProps;
    const hasError = touched[name] && Boolean(errors[name]);
    return <Component children={children} error={hasError} label={label} />;
  };

  // Dynamic field renderer
  const renderDynamicField = (
    field: FieldProps,
    formikProps: any,
    globalReadOnly: boolean | undefined
  ) => {
    const {
      label,
      name,
      type,
      required,
      props,
      visible,
      disabled,
      readOnly,
      events,
    } = field; // Include 'disabled'
    const Component = FIELD_COMPONENTS[type];
    if (!Component) {
      console.warn(`No component found for field type "${type}"`);
      return null;
    }

    const { values, errors, touched, setFieldValue, handleBlur } = formikProps;
    const hasError = touched[name] && Boolean(errors[name]);

    // Check if the field should be rendered based on the condition
    if (visible && !visible(values)) {
      return null;
    }

    // Handle props - can be static object or function
    const getFieldProps = () => {
      if (!props) return {};
      if (typeof props === 'function') {
        return props(values);
      }
      return props;
    };

    const fieldProps = getFieldProps();

    const getNestedFieldValue = (obj: any, path: string) => {
      if (!obj || !path) return undefined;
      return path
        .split(".")
        .reduce(
          (
            acc: { [x: string]: any; hasOwnProperty: (arg0: any) => any },
            key: string | number
          ) => {
            if (acc && acc.hasOwnProperty(key)) {
              return acc[key];
            }
            return undefined; // Return undefined if the key doesn't exist
          },
          obj
        );
    };

    // Use Formik's state value for the field
    const fieldValue = getNestedFieldValue(values, name);

    // Evaluate the disabled state dynamically
    const isDisabled =
      typeof disabled === "function" ? disabled(values) : disabled;
    const isReadOnly =
      typeof readOnly === "function"
        ? readOnly(values)
        : readOnly ?? globalReadOnly; // Prioritize field-specific readOnly
    const isRequired =
      typeof required === "function" ? required(values) : required;
       // Evaluate helperText if it's a function
    const evaluatedHelperText = typeof field.helperText === 'function' ? field.helperText(values) : field.helperText;

    // Handle change for different field types
    const handleChange: (event: any, index?: number) => void = (e, index) => {
      let updatedValue;
      // Utills.cl(`handleChange ${JSON.stringify(e)}`)

      // const { pageName } = formConfig;

      if (typeof e === "undefined") return;

      switch (type) {
        case "date":
          if (moment.isMoment(e) && e.isValid()) {
            updatedValue = e.format("YYYY/MM/DD");
          } else if (e instanceof Date && !isNaN(e.getTime())) {
            updatedValue = moment(e).format("YYYY/MM/DD");
          } else {
            updatedValue = e;
          }
          break;

        case "dateRange":
          // Handle date range - e should be an array [startDate, endDate]
          if (Array.isArray(e)) {
            updatedValue = e;
          } else {
            updatedValue = e;
          }
          break;

        case "checkbox":
          updatedValue = e?.target?.checked ?? e;
          break;

        case "radio":
          updatedValue = e?.target?.value ?? e;
          break;
        
        case 'search':
        case 'select':
          if (fieldProps?.multiple) {
            Utills.cl(`handleChange ${index}`)
            updatedValue = Array.isArray(e.target?.value) ? e.target.value : e;
          } else {
            updatedValue = e?.target?.value ?? e;
          }
          break;

        default:
          updatedValue = e?.target?.value ?? e;
          break;
      }
      // Utills.cl(`${pageName} - ${subPageName}`)
      // if (subPageName) {
      //   const updatedData = {
      //       [name]: updatedValue ,
      //   };
      //   if (!isNewTCRequested) {
      //     const currFormValue = getCurrentFormValues()
      // // if (pageName == "certified-products") {
      // //       if (name.toLowerCase() == ("tcProductNo").toLocaleLowerCase() && currFormValue[name] != updatedValue) {
      // //         setDeleteSave(true)
      // //         setEditSave(true)
      // //       }
      // //     }
      //     //  if (pageName == "input-tc-product-details") {
      //     //   if (name.toLowerCase() == ("inputTcNo").toLocaleLowerCase() && currFormValue[name] != updatedValue) {
      //     //     setDeleteSave(true)
      //     //     setEditSave(true)
      //     //   }
      //     // }
      //   }
      // if (!isNewScRequested) {
      //   const currFormValue = getCurrentFormValues();
      //   if (pageName == "sc-facilitydetails") {
      //     if (name.toLowerCase() == ("facilityNo").toLocaleLowerCase() && currFormValue[name] != updatedValue) {
      //       setDeleteSave(true)
      //     }
      //     else {
      //       setDeleteSave(false)
      //     }
      //   }
      // }
      // Only update Zustand store if pageName exists
      if (pageName) {
        updateField(name, updatedValue);
      }
      // }
      // else{
      // updateField(name, updatedValue);
      // }

      // Update Formik state with the new value
      setFieldValue(name, updatedValue);

      // Call custom onChange event if provided
      if (events?.onChange) {
        events.onChange(updatedValue, values, setFieldValue, index);
      }

      if (events?.onUpdate) {
        events.onUpdate(updatedValue, values, setFieldValue, index);
      }
    };

    return (
      <Field name={name} key={name}>
        {() => (
          <Tooltip title={fieldProps.tooltip || ''}>
            <Component
              label={label}
              name={name}
              required={isRequired}
              value={fieldValue} // Use Formik's value for the field
              error={hasError}
              helperText={hasError ? errors[name] : evaluatedHelperText}
              // onBlur={(e: any) => handleBlur(e)}
              setFieldValue={setFieldValue}
              onChange={handleChange}
              multiple={fieldProps.multiple}
              disabled={isDisabled} // Pass the dynamically evaluated disabled state
              readOnly={isReadOnly}
              {...fieldProps}
            />
          </Tooltip>
        )}
      </Field>
    );
  };

  return (
    <Formik
      innerRef={formRef} // Set Formik ref so parent can access submitForm if needed
      initialValues={initialValues} // Set initial values from config
      validationSchema={validationSchema}
      enableReinitialize // Allow reinitialization with new initial values
      onSubmit={(values, actions) => {
        if (formConfig.handleSubmit) {
          formConfig.handleSubmit(values); // Call handleSubmit from formConfig
        } else {
          console.warn("handleSubmit is not defined in formConfig");
        }
      }}
    >
      {(formikProps) => {
        useEffect(() => {
          formConfig.onValuesChange?.(formikProps.values);
        }, [formikProps.values]);
        const originalSetFieldValue = formikProps.setFieldValue;
        formikProps.setFieldValue = (field, value, shouldValidate = true) => {
          originalSetFieldValue(field, value, shouldValidate);
          // Only update Zustand store if pageName exists
          if (pageName) {
            updateField(field, value);
          }
        };
        return (
          <Form
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <Box sx={{ marginBottom: 3 }}>
              {sections.map((section, index) => (
                <Box
                  key={index}
                  sx={{
                    marginBottom: 2,
                    borderWidth: section?.collapsible ? "1px" : "0px",
                    borderColor: "#f0ede5",
                    borderStyle: "solid",
                    borderRadius: "0.3rem",
                    // padding: section?.collapsible ? "1rem" : "0rem",
                  }}
                >
                  {section.title && (
                    <>
                      <Typography
                        variant="body1"
                        display={section?.collapsible ? "flex" : "block"}
                        gutterBottom
                        paddingTop={2}
                        marginBottom={0}
                        borderBottom={
                          section?.collapsible && sectionFlags[index]?.isOpen
                            ? "1px #f0ede5 solid"
                            : "none"
                        }
                        justifyContent="space-between"
                        fontWeight={"600"}
                      >
                        <span
                          style={{
                            paddingLeft: section?.collapsible ? "1rem" : "0rem",
                          }}
                        >
                          {section.title}
                        </span>
                        {section?.collapsible && (
                          <span
                          style={{
                            paddingRight: section?.collapsible ? "1rem" : "0rem",
                          }}
                            onClick={() => {
                              setSectionFlags((prevFlags) =>
                                prevFlags.map((flag, i) =>
                                  i === index
                                    ? { ...flag, isOpen: !flag.isOpen }
                                    : flag
                                )
                              );
                            }}
                          >
                            {sectionFlags[index].isOpen ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </span>
                        )}
                      </Typography>
                      {/* {
                        section?.collapsible && sectionFlags[index]?.isOpen && <Divider />
                      } */}
                    </>
                  )}
                  {(sectionFlags[index]?.isOpen || !section?.collapsible) && (
                    <Grid
                      container
                      spacing={3.5}
                      sx={{ padding: section?.collapsible ? "1rem" : "0rem" }}
                    >
                      {section.fields
                        .filter(
                          (field) =>
                            !field.visible || field.visible(formikProps.values)
                        ) // Filter fields based on condition
                        .map((field) => (
                          <Grid
                            item
                            xs={field?.type==='dateRange?'? field.gridSize.xs*2 : field.gridSize.xs}
                            md={field?.type==='dateRange?'? field.gridSize.md*2 : field.gridSize.md}
                            lg={field.gridSize.lg}
                            key={field.name}
                          >
                            {field.type === "component"
                              ? renderComponents(field, formikProps)
                              : renderDynamicField(
                                  field,
                                  formikProps,
                                  readOnly
                                )}
                          </Grid>
                        ))}
                    </Grid>
                  )}
                </Box>
              ))}
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FormBuilder;
